import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { inventory } from "@/api/endpoints";
import { Spinner } from "@/components/shared/Spinner";
import { Money } from "@/components/shared/Money";
import { ContractEndBadge, RevenueSourcePill } from "@/components/shared/Pill";
import { TenancyDialog } from "./TenancyDialog";
import { DeploymentAssignDialog } from "./DeploymentAssignDialog";
import type { DeviceRow, RevenueSource } from "@/types/api";

interface Filters {
  site: string;
  gpu_type: string;
  tenant: string;
  revenue_source: RevenueSource | "";
  contract_end_within_days: string;
}

const EMPTY: Filters = {
  site: "",
  gpu_type: "",
  tenant: "",
  revenue_source: "",
  contract_end_within_days: "",
};

type SortKey =
  | "name"
  | "site"
  | "gpu_type"
  | "tenant"
  | "revenue_source"
  | "mtd_revenue"
  | "trailing_30d_revenue"
  | "last_earning_date"
  | "contract_end_date";

type SortDir = "asc" | "desc";

interface SortState {
  key: SortKey;
  dir: SortDir;
}

interface ColumnDef {
  key: SortKey;
  label: string;
  align?: "left" | "right";
  /** how to compare two rows on this column */
  cmp: (a: DeviceRow, b: DeviceRow) => number;
}

const numCmp = (x: number, y: number) => (x === y ? 0 : x < y ? -1 : 1);
const strCmp = (x: string, y: string) => x.localeCompare(y);
// nulls always sort to the bottom regardless of direction (less surprising than putting them on top in desc)
const nullable = <T,>(v: T | null | undefined): v is T => v !== null && v !== undefined && v !== "";

const COLUMNS: ColumnDef[] = [
  {
    key: "name",
    label: "Hostname",
    cmp: (a, b) => strCmp(a.name, b.name),
  },
  {
    key: "site",
    label: "Site",
    cmp: (a, b) => strCmp(a.site ?? "", b.site ?? ""),
  },
  {
    key: "gpu_type",
    label: "GPU type",
    cmp: (a, b) => strCmp(a.gpu_type ?? "", b.gpu_type ?? ""),
  },
  {
    key: "tenant",
    label: "Tenant",
    cmp: (a, b) => strCmp(a.tenant ?? "", b.tenant ?? ""),
  },
  {
    key: "revenue_source",
    label: "Source",
    cmp: (a, b) => strCmp(a.revenue_source, b.revenue_source),
  },
  {
    key: "mtd_revenue",
    label: "MTD",
    align: "right",
    cmp: (a, b) => numCmp(Number(a.mtd_revenue), Number(b.mtd_revenue)),
  },
  {
    key: "trailing_30d_revenue",
    label: "30d",
    align: "right",
    cmp: (a, b) => numCmp(Number(a.trailing_30d_revenue), Number(b.trailing_30d_revenue)),
  },
  {
    key: "last_earning_date",
    label: "Last earned",
    cmp: (a, b) => strCmp(a.last_earning_date ?? "", b.last_earning_date ?? ""),
  },
  {
    key: "contract_end_date",
    label: "Contract end",
    cmp: (a, b) => strCmp(a.contract_end_date ?? "", b.contract_end_date ?? ""),
  },
];

const DEFAULT_SORT: SortState = { key: "name", dir: "asc" };

export function InventoryGrid() {
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [editing, setEditing] = useState<DeviceRow[] | null>(null);
  const [assigning, setAssigning] = useState<DeviceRow[] | null>(null);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);

  const query = useQuery({
    queryKey: ["inventory", filters],
    queryFn: () =>
      inventory.list({
        site: filters.site || undefined,
        gpu_type: filters.gpu_type || undefined,
        tenant: filters.tenant || undefined,
        revenue_source: filters.revenue_source || undefined,
        contract_end_within_days: filters.contract_end_within_days
          ? Number(filters.contract_end_within_days)
          : undefined,
      }),
  });

  const today = useMemo(() => new Date(), []);

  const rows = useMemo(() => {
    const data = query.data ?? [];
    const col = COLUMNS.find((c) => c.key === sort.key);
    if (!col) return data;
    const sorted = [...data].sort(col.cmp);
    return sort.dir === "desc" ? sorted.reverse() : sorted;
  }, [query.data, sort]);

  const setSortKey = (key: SortKey) => {
    setSort((cur) =>
      cur.key === key
        ? { key, dir: cur.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "mtd_revenue" || key === "trailing_30d_revenue" ? "desc" : "asc" }
    );
  };

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.netbox_id)));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <FilterInput
          label="Site"
          value={filters.site}
          onChange={(v) => setFilters({ ...filters, site: v })}
        />
        <FilterInput
          label="GPU type"
          value={filters.gpu_type}
          onChange={(v) => setFilters({ ...filters, gpu_type: v })}
        />
        <FilterInput
          label="Tenant"
          value={filters.tenant}
          onChange={(v) => setFilters({ ...filters, tenant: v })}
          help="literal name, or 'none' for unassigned"
        />
        <label className="flex flex-col text-xs text-slate-500">
          <span>Revenue source</span>
          <select
            value={filters.revenue_source}
            onChange={(e) =>
              setFilters({ ...filters, revenue_source: e.target.value as RevenueSource | "" })
            }
            className="mt-0.5 rounded-md border border-slate-300 px-2 py-1 text-sm dark:bg-slate-800 dark:border-slate-700"
          >
            <option value="">any</option>
            <option value="vast">vast</option>
            <option value="contract">contract</option>
            <option value="mixed">mixed</option>
            <option value="idle">idle</option>
            <option value="unknown">unknown</option>
          </select>
        </label>
        <FilterInput
          label="Contract ending within (days)"
          value={filters.contract_end_within_days}
          onChange={(v) =>
            setFilters({ ...filters, contract_end_within_days: v.replace(/[^0-9]/g, "") })
          }
        />
        {(filters.site ||
          filters.gpu_type ||
          filters.tenant ||
          filters.revenue_source ||
          filters.contract_end_within_days) && (
          <button
            onClick={() => setFilters(EMPTY)}
            className="text-xs text-slate-500 hover:text-slate-900"
          >
            clear
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          {selected.size > 0 && (
            <>
              <button
                onClick={() => setEditing(rows.filter((r) => selected.has(r.netbox_id)))}
                className="rounded-md bg-brand-500 px-3 py-1.5 text-sm text-white hover:bg-brand-700"
              >
                Set tenant ({selected.size})
              </button>
              <button
                onClick={() => setAssigning(rows.filter((r) => selected.has(r.netbox_id)))}
                className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800"
              >
                Assign deployment ({selected.size})
              </button>
            </>
          )}
        </div>
      </div>

      {query.isLoading ? (
        <Spinner label="Loading inventory…" />
      ) : query.error ? (
        <div className="text-red-700">
          {query.error instanceof Error ? query.error.message : "failed to load"}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-800">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 dark:bg-slate-800/50 dark:text-slate-300">
              <tr>
                <th className="px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={selected.size > 0 && selected.size === rows.length}
                    onChange={selectAll}
                  />
                </th>
                {COLUMNS.map((c) => (
                  <SortableTh
                    key={c.key}
                    label={c.label}
                    active={sort.key === c.key}
                    dir={sort.key === c.key ? sort.dir : null}
                    align={c.align}
                    onClick={() => setSortKey(c.key)}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length + 1} className="px-3 py-6 text-center text-slate-400">
                    No devices match the current filters.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.netbox_id}
                  className="border-t border-slate-100 hover:bg-slate-50/60 dark:border-slate-800 dark:hover:bg-slate-800/30"
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(r.netbox_id)}
                      onChange={() => toggle(r.netbox_id)}
                    />
                  </td>
                  <td className="px-3 py-2 font-medium">
                    <a href={`/inventory/${r.netbox_id}`} className="hover:underline">
                      {r.name}
                    </a>
                    {r.asset_tag && (
                      <div className="text-[11px] text-slate-400">{r.asset_tag}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">{r.site || "—"}</td>
                  <td className="px-3 py-2">
                    {nullable(r.gpu_type) ? (
                      <span>
                        {r.gpu_count ? `${r.gpu_count}× ` : ""}
                        {r.gpu_type}
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                    {r.device_type && (
                      <div className="text-[11px] text-slate-400">{r.device_type}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => setEditing([r])}
                      className="text-left hover:underline decoration-dotted"
                    >
                      {r.tenant || <span className="text-slate-400">unassigned</span>}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <RevenueSourcePill source={r.revenue_source} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Money value={r.mtd_revenue} muted={Number(r.mtd_revenue) === 0} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Money
                      value={r.trailing_30d_revenue}
                      muted={Number(r.trailing_30d_revenue) === 0}
                    />
                  </td>
                  <td className="px-3 py-2 text-slate-500">
                    {r.last_earning_date || "—"}
                  </td>
                  <td className="px-3 py-2">
                    <ContractEndBadge end={r.contract_end_date} today={today} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <TenancyDialog
          devices={editing}
          onClose={() => {
            setEditing(null);
            setSelected(new Set());
          }}
        />
      )}
      {assigning && (
        <DeploymentAssignDialog
          devices={assigning}
          onClose={() => {
            setAssigning(null);
            setSelected(new Set());
          }}
        />
      )}
    </div>
  );
}

function SortableTh({
  label,
  active,
  dir,
  align,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: SortDir | null;
  align?: "left" | "right";
  onClick: () => void;
}) {
  const arrow = active ? (dir === "asc" ? "▲" : "▼") : "";
  return (
    <th
      className={`px-3 py-2 ${align === "right" ? "text-right" : "text-left"} cursor-pointer select-none`}
      onClick={onClick}
    >
      <span
        className={`inline-flex items-center gap-1 ${active ? "text-slate-900 dark:text-white" : "hover:text-slate-900 dark:hover:text-white"}`}
      >
        {label}
        <span className="text-[10px] w-3">{arrow}</span>
      </span>
    </th>
  );
}

function FilterInput({
  label,
  value,
  onChange,
  help,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  help?: string;
}) {
  return (
    <label className="flex flex-col text-xs text-slate-500">
      <span>{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-0.5 rounded-md border border-slate-300 px-2 py-1 text-sm dark:bg-slate-800 dark:border-slate-700"
        placeholder={help}
      />
    </label>
  );
}
